import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { getPublicCommunitySummary } from '../firebase/api';
import './CommunitySummary.css';

/** 直近何ヶ月分を集計するか（UIでは切り替えず変数で指定する） */
const SUMMARY_MONTHS = Math.max(
  1,
  parseInt(process.env.REACT_APP_SUMMARY_MONTHS || '6', 10) || 1
);

/** phase_tags.tag = 画像ファイル名（拡張子なし）・バックエンドの集計キーと一致 */
const PHASE_TAG_ORDER = [
  'reaction',
  'fact',
  'discovery',
  'grope',
  'reflection',
  'question',
  'analysis',
  'outcome',
  'insight',
  'intention',
  'trial-and-error',
  'idea',
];

const PHASE_TAG_LABELS = {
  reaction: '感想',
  fact: '記録',
  discovery: '気付き',
  grope: '模索',
  reflection: '内省',
  question: '問い',
  analysis: '分析',
  outcome: '到達',
  insight: '洞察',
  intention: '宣言',
  'trial-and-error': '試行錯誤',
  idea: 'アイデア',
};

const PHASE_TAG_DESCRIPTIONS = {
  reaction: '生の気持ちや出来事への反応',
  fact: '客観的な出来事や状態のメモ',
  discovery: '「あ、そうか」と発見したこと',
  grope: '結論の出ない迷いや模索',
  reflection: '自分を一歩引いて観察する内省',
  question: '解決したい課題や純粋な「なぜ？」',
  analysis: '根拠から構造や因果を解明する分析',
  outcome: '継続的な試行錯誤からのある状態への到達',
  insight: '人生全般に言える教訓への昇華',
  intention: '具体的な行動計画や自分への誓い',
  'trial-and-error': '方法を試し改善を繰り返す試行錯誤',
  idea: '独創的な解決策や未来の可能性',
};

const phaseOrderIndex = (tag) => {
  const i = PHASE_TAG_ORDER.indexOf(tag);
  return i === -1 ? 999 : i;
};

/** ランキング1行に並べるアイコンの最大数（超える場合は1アイコン≒N件に縮約） */
const MAX_RANK_ICONS = 10;

const buildPhaseRanking = (phaseCounts) => {
  if (!phaseCounts || typeof phaseCounts !== 'object') return { rows: [], unit: 1 };
  const entries = PHASE_TAG_ORDER.map((tag) => ({
    tag,
    count: Math.max(0, Number(phaseCounts[tag]) || 0),
  })).filter((e) => e.count > 0);
  entries.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return phaseOrderIndex(a.tag) - phaseOrderIndex(b.tag);
  });
  if (entries.length === 0) return { rows: [], unit: 1 };
  const maxCount = entries[0].count;
  const unit = Math.max(1, Math.ceil(maxCount / MAX_RANK_ICONS));
  const rows = entries.map((e, idx) => ({
    ...e,
    rank: idx + 1,
    iconCount: Math.max(1, Math.ceil(e.count / unit)),
  }));
  return { rows, unit };
};

const MIN_TAG_FONT_PX = 12;
const MAX_TAG_FONT_PX = 30;

/** 出現数の対数スケールでタグクラウドのフォントサイズを決める（TopicTagsAggregate と同方式） */
const computeTagFontSize = (count, counts) => {
  if (!counts.length) return (MIN_TAG_FONT_PX + MAX_TAG_FONT_PX) / 2;
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  if (max === min) return (MIN_TAG_FONT_PX + MAX_TAG_FONT_PX) / 2;
  const logMin = Math.log(min);
  const logMax = Math.log(max);
  const t = (Math.log(count) - logMin) / (logMax - logMin);
  return MIN_TAG_FONT_PX + t * (MAX_TAG_FONT_PX - MIN_TAG_FONT_PX);
};

const CommunitySummary = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showPhaseHelp, setShowPhaseHelp] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const questionsRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getPublicCommunitySummary({ months: SUMMARY_MONTHS });
        if (!cancelled) setSummary(res);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const { rows: phaseRows, unit: phaseUnit } = useMemo(
    () => buildPhaseRanking(summary?.phase_counts),
    [summary]
  );

  const topicSubjects = useMemo(
    () => (Array.isArray(summary?.topic_subjects) ? summary.topic_subjects : []),
    [summary]
  );
  const topicSubjectCounts = useMemo(
    () => topicSubjects.map((t) => t.count),
    [topicSubjects]
  );

  const specialQuestions = useMemo(
    () => (Array.isArray(summary?.special_questions) ? summary.special_questions : []),
    [summary]
  );

  const scrollToQuestions = () => {
    questionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToAbout = () => {
    aboutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** 「書いてみよう！」リンク: ログイン済みなら Top、未ログインなら Start へ */
  const handleWriteLead = () => {
    navigate(currentUser ? '/top' : '/start');
  };

  /** 問いかけクリック: ログイン済みなら選択状態でポスター作成へ（Top.js の handleQuestionClick と同形式） */
  const handleQuestionClick = (q) => {
    if (currentUser) {
      navigate('/poster-create', {
        state: {
          question: { id: q.id, title: q.title, icon: '/img/cat.png' },
          isQuestionBased: true,
        },
      });
    } else {
      navigate('/start');
    }
  };

  const handlePhaseImageError = (e) => {
    e.target.src = '/img/placeholder.png';
    e.target.onerror = null;
  };

  return (
    <div className="community-summary-page">
      {/* ヘッダー: ログイン状態で出し分け（他ページの .top-header と同トーン） */}
        <header className="community-summary-header">
          <div className="community-summary-logo">
            <img
              src="/img/tiny-identity.png"
              alt=""
              className="community-summary-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="community-summary-logo-text">Tiny Identity</span>
          </div>
          {currentUser ? (
            <div className="community-summary-header-user">
              <img
                src={userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'}
                alt="プロフィール画像"
                title={`${userProfile?.displayName || userProfile?.nickname || 'ユーザー'} さん`}
                className="community-summary-user-icon"
                onError={(e) => {
                  e.target.src = '/img/icon.png';
                }}
              />
              <button
                type="button"
                className="community-summary-menu-btn"
                aria-label="メニュー"
                aria-expanded={showMenu}
                onClick={() => setShowMenu((v) => !v)}
              >
                <span />
                <span />
                <span />
              </button>
              {showMenu && (
                <>
                  <div
                    className="community-summary-menu-backdrop"
                    onClick={() => setShowMenu(false)}
                  />
                  <nav className="community-summary-menu">
                    <button
                      type="button"
                      className="community-summary-menu-item"
                      onClick={() => {
                        setShowMenu(false);
                        navigate('/top');
                      }}
                    >
                      ポスターを作成する
                    </button>
                    <button
                      type="button"
                      className="community-summary-menu-item"
                      onClick={() => {
                        setShowMenu(false);
                        navigate('/list');
                      }}
                    >
                      ポスター一覧へ
                    </button>
                    <button
                      type="button"
                      className="community-summary-menu-item"
                      onClick={() => {
                        setShowMenu(false);
                        if (window.confirm('ログアウトしますか？')) logout();
                      }}
                    >
                      ログアウト
                    </button>
                  </nav>
                </>
              )}
            </div>
          ) : (
            <div className="community-summary-header-actions">
              <button
                type="button"
                className="community-summary-btn-outline"
                onClick={() => navigate('/login')}
              >
                ログイン
              </button>
              <button
                type="button"
                className="community-summary-btn-dark"
                onClick={() => navigate('/regist')}
              >
                会員登録
              </button>
            </div>
          )}
        </header>

        <div className="community-summary-body">
          <div className="community-summary-top-links">
            <button
              type="button"
              className="community-summary-top-link"
              onClick={scrollToAbout}
            >
              Tiny Identityとは？
            </button>
            {specialQuestions.length > 0 && (
              <button
                type="button"
                className="community-summary-top-link"
                onClick={scrollToQuestions}
              >
                今月の問いかけはこちら
              </button>
            )}
          </div>

          <h1 className="community-summary-title">
            みんなのちょっとした叫び　直近{SUMMARY_MONTHS}ヶ月のサマリー
          </h1>

          {loading ? (
            <div className="community-summary-loading">読み込み中...</div>
          ) : error ? (
            <div className="community-summary-loading">
              サマリーの取得に失敗しました。時間をおいて再度お試しください。
            </div>
          ) : (
            <>
              {/* 思考フェーズランキング */}
              <section className="community-summary-section">
                <div className="community-summary-section-heading">
                  <span className="community-summary-section-badge">思考フェーズ</span>
                  <button
                    type="button"
                    className="community-summary-help-btn"
                    aria-label="思考フェーズとは"
                    onClick={() => setShowPhaseHelp((v) => !v)}
                  >
                    ?
                  </button>
                </div>

                {showPhaseHelp && (
                  <div
                    className="community-summary-modal-overlay"
                    onClick={() => setShowPhaseHelp(false)}
                  >
                    <div
                      className="community-summary-phase-help"
                      role="dialog"
                      aria-label="思考フェーズとは"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="community-summary-modal-header">
                        <h3>思考フェーズとは？</h3>
                        <button
                          type="button"
                          className="community-summary-modal-close"
                          aria-label="閉じる"
                          onClick={() => setShowPhaseHelp(false)}
                        >
                          ×
                        </button>
                      </div>
                      <p>
                        コメントの内容から、AIが「どんな思考のモードで書かれたか」を鑑定して付けるタグです。
                      </p>
                      <ul>
                        {PHASE_TAG_ORDER.map((slug) => (
                          <li key={slug}>
                            <img
                              src={`/img/phase-tags/${slug}.png`}
                              alt=""
                              onError={handlePhaseImageError}
                            />
                            <strong>{PHASE_TAG_LABELS[slug]}</strong>
                            <span>{PHASE_TAG_DESCRIPTIONS[slug]}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {phaseRows.length === 0 ? (
                  <div className="community-summary-empty">
                    {summary?.comments_in_period === 0
                      ? 'この期間にはまだコメントがありません'
                      : '思考フェーズが付いたコメントはまだありません'}
                  </div>
                ) : (
                  <>
                    <div className="community-summary-phase-ranking">
                      {phaseRows.map((row) => (
                        <div key={row.tag} className="community-summary-phase-row">
                          <span className="community-summary-phase-rank-label">
                            <span className="community-summary-phase-rank-num">
                              {row.rank}位
                            </span>
                            <span className="community-summary-phase-rank-name">
                              {PHASE_TAG_LABELS[row.tag] || row.tag}
                            </span>
                          </span>
                          <span className="community-summary-phase-icons">
                            {Array.from({ length: row.iconCount }).map((_, i) => (
                              <img
                                key={`${row.tag}-${i}`}
                                src={`/img/phase-tags/${row.tag}.png`}
                                alt={i === 0 ? PHASE_TAG_LABELS[row.tag] || row.tag : ''}
                                title={`${PHASE_TAG_LABELS[row.tag] || row.tag}: ${row.count}件`}
                                className="community-summary-phase-icon"
                                onError={handlePhaseImageError}
                              />
                            ))}
                          </span>
                        </div>
                      ))}
                    </div>
                    {phaseUnit > 1 && (
                      <p className="community-summary-phase-unit-note">
                        アイコン1つ ≒ {phaseUnit}件
                      </p>
                    )}
                  </>
                )}
              </section>

              {/* Topic Subjects タグクラウド */}
              <section className="community-summary-section">
                <div className="community-summary-section-heading">
                  <span className="community-summary-section-badge community-summary-section-badge-en">
                    Topic Subjects
                  </span>
                </div>
                {topicSubjects.length === 0 ? (
                  <div className="community-summary-empty">
                    この期間にはまだ話題タグがありません
                  </div>
                ) : (
                  <>
                    <div className="community-summary-tags-lead-wrap">
                      <button
                        type="button"
                        className="community-summary-tags-lead"
                        onClick={handleWriteLead}
                      >
                        気になるワードがあれば関係する出来事や思いを書いてみよう！
                      </button>
                    </div>
                    <div
                      className="community-summary-tag-cloud"
                      role="list"
                      aria-label="話題対象タグクラウド"
                    >
                      {topicSubjects.map(({ tag, count }) => (
                        <span
                          key={tag}
                          role="listitem"
                          className="community-summary-tag"
                          style={{ fontSize: `${computeTagFontSize(count, topicSubjectCounts)}px` }}
                          title={`${tag}: ${count}件のコメント`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </section>
            </>
          )}
        </div>

        {/* Tiny Identity とは？ */}
        <section className="community-summary-about" ref={aboutRef}>
          <h2 className="community-summary-about-title">Tiny Identity とは？</h2>
          <p className="community-summary-about-text">
            日々の出来事に対する感情や気づき・疑問・考えなど、ちょっとした叫びをポスターにして記録します。
            それについてきちんとした文章じゃなくても1行でもいいのでコメントを添えると、AIから「思考フェーズ猫」が付与されます。
          </p>
          <p className="community-summary-about-text">
            さらに同種のトピックや類似の体験について書かれた過去の自分の投稿や
            他のユーザの投稿が Suggest され、関連付けすることができます。それをヒントに前と違った思考フェーズで捉えて、
            また書いてみる。
          </p>
          <p className="community-summary-about-text">
            これを繰り返す中で、一つの小さな感情や考えがいろんな方向に育っていきます。
            思考フェーズキャラを集めながら楽しく自分の世界を耕していきましょう。
            思考の世界は居場所になり、深いところでのつながりは自分の世界を広げてくれるはず・・
          </p>
          <div className="community-summary-about-links">
            <button
              type="button"
              className="community-summary-about-link"
              onClick={() => navigate(currentUser ? '/top' : '/start')}
            >
              使ってみる
            </button>
            <a
              className="community-summary-about-link"
              href="/lp.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              もっと知る
            </a>
          </div>
        </section>

        {/* 今月の問いかけ */}
        {specialQuestions.length > 0 && (
          <section className="community-summary-questions" ref={questionsRef}>
            <span className="community-summary-questions-badge">今月の問いかけ</span>
            <ul className="community-summary-questions-list">
              {specialQuestions.map((q, idx) => (
                <li key={q.id} className="community-summary-question-item">
                  <img
                    src="/img/cat.png"
                    alt=""
                    className={`community-summary-question-icon community-summary-question-icon-${(idx % 3) + 1}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    className="community-summary-question-title"
                    onClick={() => handleQuestionClick(q)}
                  >
                    {q.title}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
    </div>
  );
};

export default CommunitySummary;
